'use client';

// lib/pricing/useExternalPricing.ts
// React hook that fetches per-customer prices for a result set.
//
// Used by ProductCard / ProductGrid to swap in real customer prices after
// search results render. Gated by features.externalPricing — no-op for clients
// that don't have B2B per-customer pricing (parousiasi, kaizershop, etc).

import { useEffect, useRef, useState } from 'react';
import type { ClientConfig } from '@/lib/clients/types';
import { fetchCustomerPrices, type NormalizedPrice } from './externalPricing';
import { chunkIds, PRICE_CHUNK_SIZE } from './externalPricing';

/**
 * How many ids the FIRST pricing request covers — the products actually on
 * screen. Matches `INITIAL_VISIBLE` in ProductSection (the grid renders 100
 * cards before "show more"), so one small request prices everything the user
 * can see; the remainder streams in behind it. Keep the two in step: raise
 * INITIAL_VISIBLE without raising this and the first screen paints half-priced.
 */
export const PRICE_PRIORITY_WINDOW = 100;

/**
 * Fallback budget for one interactive display-price request.
 *
 * Used only when the client's config does not supply `timeoutMs`. A logged-in
 * pricing batch runs per-id tax/B2B computation on the HOST, so it is legitimately
 * slow — measured on syfak's staging box at ~57ms per product id, i.e. ~6s for the
 * 100-id priority window. Anything in the low seconds guarantees failure rather
 * than protecting anything.
 */
export const DEFAULT_DISPLAY_PRICE_TIMEOUT_MS = 10_000;

/**
 * Do the host's product code and our indexed `model` refer to the same product?
 *
 * Lenient by design in one specific way: an exact match wins, and failing that
 * we retry with leading zeros stripped from both sides, so a purely cosmetic
 * "01267" vs "1267" difference does NOT blank a valid price. Anything beyond
 * that (108794 vs 108847) is treated as a genuine mismatch.
 *
 * Returns TRUE whenever either side is missing — an unverifiable product must
 * keep its price, or clients whose endpoint never returns a code would lose
 * pricing entirely. The guard only ever acts on POSITIVE evidence of drift.
 */
export function productCodesMatch(
  hostCode: string | null | undefined,
  indexedModel: string | null | undefined,
): boolean {
  if (!hostCode || !indexedModel) return true;
  const a = String(hostCode).trim().toLowerCase();
  const b = String(indexedModel).trim().toLowerCase();
  if (!a || !b) return true;
  if (a === b) return true;
  const stripZeros = (v: string) => v.replace(/^0+/, '');
  const sa = stripZeros(a);
  const sb = stripZeros(b);
  // Both collapsing to empty means both were all-zeros — treat as equal, not
  // as a vacuous match against an empty string.
  if (!sa && !sb) return true;
  if (!sa || !sb) return false;
  return sa === sb;
}

/**
 * Per-card price resolver. Mirrors the three states ProductCard understands:
 *
 *  - external pricing not enabled       → undefined  (card renders RRP unchanged)
 *  - enabled, fetch in flight or failed → undefined  (card renders RRP as fallback)
 *  - enabled, ID present in response    → object     (card shows customer price)
 *  - enabled, ID absent or [false,false]→ null       (card shows unavailable label)
 *
 * INTEGRITY GUARD (added 2026-07-27). Per-customer prices are matched to
 * products purely by numeric id, shared between our index (built from the live
 * feed) and the host's shop database. Those two id spaces are ASSUMED to agree
 * and we cannot test that assumption before go-live — the endpoint only answers
 * on the host's own page, to a logged-in customer. Real drift has already been
 * observed on syfak's staging DB (older snapshot than the live feed: id 39481
 * resolved to different products on each side, 7.76€ vs 16,97€).
 *
 * So when the response reveals the host's own product code, we check it against
 * our indexed `model` and DROP the price on a mismatch — the card falls back to
 * its no-price state rather than showing a confident, wrong number. A missing
 * price is a support call; a wrong price is a wrong order. Pass `indexedModel`
 * to arm the guard; omit it and behaviour is exactly as before.
 */
export function resolveCustomerPrice(
  active: boolean,
  prices: Map<string, NormalizedPrice> | null,
  productId: string | number,
  indexedModel?: string | null,
): NormalizedPrice | null | undefined {
  if (!active) return undefined;
  if (!prices) return undefined;
  const entry = prices.get(String(productId));
  if (!entry) return null;
  if (entry.regular === null && entry.sale === null) return null;
  if (!productCodesMatch(entry.productCode, indexedModel)) {
    // Loud on purpose: id-space drift is systemic, not per-product, so the
    // first of these in a console is the signal that the host's catalogue and
    // our index have diverged and EVERY price in that range is suspect.
    console.warn(
      `[pricing] product-code mismatch for id ${productId}: host sent "${entry.productCode}", index has "${indexedModel}" — price suppressed (possible catalogue drift)`,
    );
    return null;
  }
  return entry;
}

export interface UseExternalPricingResult {
  /**
   * Map of product_id (string) → NormalizedPrice. `null` while loading or when
   * the feature is disabled. An empty map means the call returned but had no
   * usable prices (rare).
   */
  prices: Map<string, NormalizedPrice> | null;
  /** True only while a fetch is in flight. */
  loading: boolean;
  /** Human-readable error message, or null. UI should fall back to RRP on error. */
  error: string | null;
  /**
   * The joined id-list these prices were fetched FOR. Consumers must treat
   * `prices` as valid only when this equals their CURRENT id list: state
   * updates lag one paint behind prop changes, so on the first results of a
   * query the hook still holds the PREVIOUS set's prices (initially an empty
   * map) for one frame — filtering the new products against it hides
   * everything and flashes a bogus no-results screen (founder-observed on
   * the beta, first price-filtered query of a session).
   */
  forIdsKey: string;
}

/**
 * Fetch per-customer prices for the given product IDs whenever the IDs change.
 *
 * Behaviors:
 * - If the client has `features.externalPricing === false` (or no
 *   `externalPricing` config), this hook is a no-op:
 *   `{ prices: null, loading: false, error: null }`.
 * - Errors and timeouts surface through `error` — UI falls back to RRP.
 * - Re-fetches when the set of IDs changes (joined string is the dep).
 * - Cancels in-flight requests on unmount or dep change.
 */
export function useExternalPricing(
  productIds: Array<string | number>,
  config: ClientConfig,
): UseExternalPricingResult {
  const enabled = !!(config.features?.externalPricing && config.externalPricing);
  const idsKey = productIds.join(',');

  const [state, setState] = useState<UseExternalPricingResult>({
    prices: null,
    loading: enabled && productIds.length > 0,
    error: null,
    forIdsKey: '',
  });

  // What we have already asked the host for, so a scroll can fetch only the
  // newly revealed products. `count`/`key` describe the id list covered so far;
  // `map` holds the prices it produced. A ref, not state: updating it must not
  // itself trigger a render, and it has to survive the effect it feeds.
  const fetchedRef = useRef<{ count: number; key: string; map: Map<string, NormalizedPrice> }>({
    count: 0,
    key: '',
    map: new Map(),
  });

  useEffect(() => {
    if (!enabled) {
      fetchedRef.current = { count: 0, key: '', map: new Map() };
      setState({ prices: null, loading: false, error: null, forIdsKey: idsKey });
      return;
    }
    if (productIds.length === 0) {
      fetchedRef.current = { count: 0, key: '', map: new Map() };
      setState({ prices: new Map(), loading: false, error: null, forIdsKey: idsKey });
      return;
    }

    // EXTENDING vs STARTING OVER (2026-07-28, lazy fill).
    //
    // The parent now passes only the products currently REVEALED in the grid,
    // and that list grows by a batch each time the shopper scrolls. Growing it
    // must not re-ask the host for prices we already hold: a scroll would
    // otherwise re-request the first 100 products on every batch, which is the
    // opposite of the saving. So when the new list is the old list plus more on
    // the end, we fetch ONLY the new ids and merge them into what we have.
    //
    // Anything that is NOT the same ids plus more on the end starts clean. A
    // new search normally lands here: the grid resets its revealed count to one
    // batch, so the shorter list fails the length test. The one case that does
    // reuse across searches is a new list whose leading ids are byte-identical
    // to what we already fetched — and there reuse is correct, since it is the
    // same products for the same customer moments earlier. This is NOT the
    // general session cache (backlog 6007b867); that one spans arbitrary
    // searches and needs a staleness policy this deliberately does not assume.
    const prev = fetchedRef.current;
    const isExtension =
      prev.count > 0 &&
      productIds.length >= prev.count &&
      productIds.slice(0, prev.count).join(',') === prev.key;
    const idsToFetch = isExtension ? productIds.slice(prev.count) : productIds;
    const baseMap = isExtension ? new Map(prev.map) : new Map<string, NormalizedPrice>();

    if (isExtension && idsToFetch.length === 0) {
      // Same revealed set, re-rendered. Nothing to ask for.
      setState({ prices: baseMap, loading: false, error: null, forIdsKey: idsKey });
      return;
    }

    let cancelled = false;
    // Superseding a search must STOP its requests, not merely ignore their
    // answers (2026-07-27). The old `cancelled` flag only guarded setState, so
    // typing another letter left the previous query's request running on the
    // host — and a second request meeting a busy host is exactly what returns
    // 503. This signal aborts the live request and lets any of its still-queued
    // chunks skip their turn.
    const abort = new AbortController();
    // Extending keeps the prices already on screen visible while the new batch
    // loads; starting over clears them (a different result set's prices must
    // never be shown against new products).
    setState({ prices: isExtension ? baseMap : null, loading: true, error: null, forIdsKey: idsKey });

    // PRIORITY-FIRST, SEQUENTIAL, PARTIAL-TOLERANT (rewritten 2026-07-27).
    //
    // What it replaced and why. This used to be one `Promise.all` over
    // equal chunkSize chunks. On a broad query (founder: «καλλυντικα» → 1,000
    // hits) that fired 2×500-id requests at once; syfak answered the first 200
    // and the second **503**, `Promise.all` rejected, and the catch discarded
    // the map — so the 500 prices we HAD already been given were thrown away
    // too. Every card fell back to RRP with no offers and no status tags,
    // which is exactly what the founder saw. Three faults, all fixed here:
    //
    //  1. ALL-OR-NOTHING → per-chunk try/catch. A refused batch costs only its
    //     own ids; everything already fetched is kept and rendered.
    //  2. SLOWEST-FIRST → the first request covers only the ids that are
    //     actually on screen (PRICE_PRIORITY_WINDOW, matching ProductSection's
    //     INITIAL_VISIBLE), so real prices paint after ONE small request
    //     instead of after the whole catalogue-sized set resolves.
    //  3. CONCURRENT → sequential. The host processes one request per session
    //     at a time anyway (see the price-map starvation note in the B2B doc),
    //     so parallelism bought nothing and was what tripped the 503.
    //
    // Each chunk emits as it lands, so the grid fills progressively. Consumers
    // distinguish "not fetched yet" from "no price" by MAP MEMBERSHIP: the
    // endpoint echoes every requested id (unknown ones as [false,false]), so an
    // id absent from the map is still in flight → the card shows its loading
    // skeleton rather than a catalogue price that would later change under the
    // user (founder's call), or a wrong "unavailable".
    const pricing = config.externalPricing!;
    // A pure extension is already "what's on screen next" — it needs no
    // priority window, just normal batches. A fresh set still leads with the
    // on-screen window so the first paint costs ONE small request.
    const chunks = (
      isExtension
        ? chunkIds(idsToFetch, pricing.chunkSize ?? PRICE_CHUNK_SIZE)
        : [
            idsToFetch.slice(0, PRICE_PRIORITY_WINDOW),
            ...chunkIds(idsToFetch.slice(PRICE_PRIORITY_WINDOW), pricing.chunkSize ?? PRICE_CHUNK_SIZE),
          ]
    ).filter((c) => c.length > 0);

    void (async () => {
      const merged = new Map<string, NormalizedPrice>(baseMap);
      let lastError: string | null = null;
      for (const chunk of chunks) {
        if (cancelled) return;
        try {
          // Budget comes from the CLIENT CONFIG, falling back to 10s.
          //
          // This used to hardcode 10_000, which silently overrode whatever the
          // config said — so `timeoutMs` looked configurable in three places
          // (syfak.ts, acalight.ts and the DB rows, all saying 3000) while being
          // read by nothing. Same trap as typoTolerance in 2026-08: a setting that
          // appears configured but isn't is worse than no setting, because the
          // next person reads it and believes it.
          //
          // ⚠️ The DB rows must be >= the real cost of a request before this line
          // can be trusted — they were raised to 10000 on dev and prod first.
          const part = await fetchCustomerPrices(chunk, pricing, {
            timeoutMs: pricing.timeoutMs || DEFAULT_DISPLAY_PRICE_TIMEOUT_MS,
            signal: abort.signal,
          });
          for (const [k, v] of part) merged.set(k, v);
        } catch (err) {
          // Keep going — this chunk's ids simply stay unpriced. Losing 500
          // prices because the 501st batch was refused is the bug we are fixing.
          lastError = err instanceof Error ? err.message : 'pricing fetch failed';
          console.warn(`[pricing] chunk of ${chunk.length} failed:`, lastError);
        }
        if (cancelled) return;
        // Emit progressively — a NEW Map each time so React sees the change.
        setState({ prices: new Map(merged), loading: true, error: lastError, forIdsKey: idsKey });
      }
      if (cancelled) return;
      // Record what is now covered, so the next scroll asks only for the batch
      // beyond it. Ids whose chunk FAILED are still counted as covered: they are
      // not retried within a result set today either, and once loading ends an
      // absent id correctly renders as unavailable rather than a stuck skeleton.
      fetchedRef.current = { count: productIds.length, key: idsKey, map: merged };
      setState({ prices: merged, loading: false, error: lastError, forIdsKey: idsKey });
    })();

    return () => {
      cancelled = true;
      abort.abort();
    };
    // We intentionally depend on the joined ID string (stable across renders
    // when contents are equal) and the endpoint URL, not the array reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, idsKey, config.externalPricing?.endpointUrl]);

  return state;
}
